/*
 * Copyright (c) 2016-2023 VMware, Inc. All Rights Reserved.
 * This software is released under MIT license.
 * The full license information can be found in LICENSE in the root directory of this project.
 */
import { listenForAttributeChange } from '../utils/events.js';
import { TriggerController } from './trigger.controller.js';
/**
 * Provides all nessesary aria-* attributes to create a vaild aria popup.
 * Used in combination of the `@ariaPopupTrigger` controller.
 */
export function ariaPopup() {
    return (target) => target.addInitializer((instance) => new AriaPopupController(instance));
}
export class AriaPopupController {
    constructor(host) {
        this.host = host;
        this.host.addController(this);
        this.trigger = new TriggerController(this.host, { focus: false });
    }
    async hostConnected() {
        await this.host.updateComplete;
        this.observer = listenForAttributeChange(this.host, 'hidden', () => this.updateTrigger(!this.host.hidden));
    }
    async hostUpdate() {
        await this.host.updateComplete;
        this.updateTrigger(!this.host.hidden);
    }
    hostDisconnected() {
        this.updateTrigger(false);
        this.observer?.disconnect();
    }
    updateTrigger(expanded) {
        if (this.trigger.current?.hasAttribute('aria-controls')) {
            this.trigger.current.ariaExpanded = `${expanded}`;
        }
        if (this.trigger.prev?.hasAttribute('aria-controls') && this.trigger.prev !== this.trigger.current) {
            this.trigger.prev.ariaExpanded = 'false';
        }
    }
}
